/** Personal bests outside the high-score tables: Practice, Daily Dig, Expedition (docs/spec/07 §2). */

import type { KeyValueStore } from './storage';
import { KEYS } from './storage';

/** The key a Practice best is kept under. */
export function practiceKey(def: { id: string; name: string }, level: number): string {
  return `${def.id}:${def.name}:${level}`;
}

export interface DailyRecord {
  readonly day: number;
  readonly attempts: number;
  /** Best score on a successful run (0 if never finished). */
  readonly best: number;
  /** Seconds taken on that best run. */
  readonly bestTime: number;
  readonly diamonds: number;
  readonly won: boolean;
}

export interface ExpeditionRecord {
  readonly depth: number;
  readonly score: number;
}

function read<T>(store: KeyValueStore, key: string, fallback: T): T {
  try {
    const raw = store.load(key);
    return raw ? { ...fallback, ...(JSON.parse(raw) as T) } : fallback;
  } catch {
    return fallback;
  }
}

export class Records {
  constructor(private readonly store: KeyValueStore) {}

  /** Best score for a Practice cave at a level, or 0. `key` is e.g. `classic:A:1`. */
  practiceBest(key: string): number {
    const all = read<Record<string, number>>(this.store, KEYS.practice, {});
    return all[key] ?? 0;
  }

  recordPractice(key: string, score: number): boolean {
    const all = read<Record<string, number>>(this.store, KEYS.practice, {});
    if (score <= (all[key] ?? 0)) return false;
    this.store.save(KEYS.practice, JSON.stringify({ ...all, [key]: score }));
    return true;
  }

  daily(day: number): DailyRecord {
    const r = read<DailyRecord>(this.store, KEYS.daily, { day, attempts: 0, best: 0, bestTime: 0, diamonds: 0, won: false });
    return r.day === day ? r : { day, attempts: 0, best: 0, bestTime: 0, diamonds: 0, won: false };
  }

  /** Count an attempt, and keep it if it is the day's best finished run. */
  recordDaily(day: number, run: { won: boolean; score: number; seconds: number; diamonds: number }): DailyRecord {
    const r = this.daily(day);
    const better = run.won && (!r.won || run.score > r.best);
    const next: DailyRecord = better
      ? { day, attempts: r.attempts + 1, best: run.score, bestTime: run.seconds, diamonds: run.diamonds, won: true }
      : { ...r, attempts: r.attempts + 1 };
    this.store.save(KEYS.daily, JSON.stringify(next));
    return next;
  }

  expedition(): ExpeditionRecord {
    return read<ExpeditionRecord>(this.store, KEYS.expedition, { depth: 0, score: 0 });
  }

  recordExpedition(depth: number, score: number): void {
    const r = this.expedition();
    this.store.save(KEYS.expedition, JSON.stringify({ depth: Math.max(r.depth, depth), score: Math.max(r.score, score) }));
  }
}
